import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonBar, ScreenHeader } from '../../components';
import { useScrollToTop } from '../../hooks';

import { NewPOAContext } from './new-poa-composer';

export function NewPOAMultipleAgents() {
  const navigate = useNavigate();
  const { updateCheckpoint } = useContext(NewPOAContext);
  
  useScrollToTop();
  
  const handleBack = useCallback(() => {
    navigate('../choosing-agent');
  }, [ navigate ]);

  const handleNext = useCallback(async () => {
    updateCheckpoint(location.pathname);
    localStorage.setItem('poa:checkpoint', location.pathname);
    navigate('../number-agents');
  }, [ navigate, updateCheckpoint ]);
  
  return (
    <div className="new-poa-multiple-agents screen">
      <ScreenHeader
        title="Naming Multiple Agents"
        description="Understanding your options for appointing one or more agents"
        handleBack={handleBack}
      />
      
      <div className="content-section">
        <h3>Single vs. Multiple Agents</h3>
        <p>
          You have the option to name one agent or multiple agents to handle your financial affairs. 
          Each approach has its own advantages and considerations.
        </p>

        <div className="agent-options">
          <div className="option-card">
            <h4>Single Agent</h4>
            <p>Appoint one person to handle all your financial matters</p>
            <ul>
              <li>Simpler decision-making process</li>
              <li>Clear responsibility and accountability</li>
              <li>Easier to communicate with one person</li>
              <li>Less potential for conflicts between agents</li>
            </ul>
          </div>

          <div className="option-card">
            <h4>Multiple Agents</h4>
            <p>Appoint two or more people to work together on your finances</p>
            <ul>
              <li>Shared responsibility and workload</li>
              <li>Built-in backup if one agent becomes unavailable</li>
              <li>Different agents can specialize in different areas</li>
              <li>Additional oversight and checks and balances</li>
            </ul>
          </div>
        </div>

        <h3>How Multiple Agents Work Together</h3>
        <p>
          When you name multiple agents, they typically must work together and agree on decisions. 
          This means:
        </p>
        <ul className="multiple-agent-details">
          <li>All agents must sign documents and make decisions jointly</li>
          <li>If one agent is unavailable, the others cannot act alone</li>
          <li>This provides built-in oversight but may slow down decision-making</li>
          <li>Consider whether your chosen agents work well together</li>
        </ul>

        <h3>Choosing the Right Number</h3>
        <p>Consider these factors when deciding how many agents to name:</p>
        <ul className="decision-factors">
          <li><strong>Your family situation:</strong> Do you have multiple trusted family members?</li>
          <li><strong>Complexity of finances:</strong> Are your financial affairs simple or complex?</li>
          <li><strong>Agent availability:</strong> Will your agents be available when needed?</li>
          <li><strong>Geographic location:</strong> Do your potential agents live in different areas?</li>
          <li><strong>Personality compatibility:</strong> Will your agents work well together?</li>
        </ul>

        <div className="important-note">
          <h4>Important:</h4>
          <p>
            You can always name alternate agents who will step in if your primary agents 
            become unavailable or unable to serve.
          </p>
        </div>
      </div>

      <ButtonBar 
        handlePrimary={handleNext}
        primaryText="Continue"
      />
    </div>
  );
}
